import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import './style.scss';
import { forwardRef, useEffect, useRef, useState } from 'react';
import RemoveIcon from '../../assets/icons/close.svg';
import ChevronIcon from '../../assets/icons/chevron.svg';
import { useTranslation } from 'react-i18next';
import { setIsModalOpen, setModalContent } from '../../store/slices/global';
import { useAppDispatch } from '../../store';
import { useCloseSelect } from '../../hooks/closeSelect';
import Tooltip from '../Tooltip';
import InfoIcon from '../../assets/icons/info.svg';
const Select = forwardRef(({ placeholder, label, options, defaultSelectedOptionValue = null, onChange, background = 'white', fontSize = 'default', size = 'default', zIndex = 1, manageIsSelected = true, highlightSelected = true, disabled, error, errorMessage, offlineChangeDisable, offlineChangeModalContent, tooltipContent, }, ref) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOptionValue, setSelectedOptionValue] = useState(defaultSelectedOptionValue);
    const [wasChanged, setWasChanged] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [openedByKeyboard, setOpenedByKeyboard] = useState(false);
    const dropdownRef = useRef(null);
    useCloseSelect(dropdownRef, () => {
        setIsOpen(false);
        setFocusedIndex(-1);
    });
    useEffect(() => {
        if (defaultSelectedOptionValue) {
            setSelectedOptionValue(defaultSelectedOptionValue);
        }
        if (wasChanged) {
            setSelectedOptionValue(defaultSelectedOptionValue);
        }
    }, [defaultSelectedOptionValue]);
    const onOptionChange = (selectedOptionValue) => {
        if (offlineChangeDisable) {
            if (navigator.onLine) {
                setSelectedOptionValue(selectedOptionValue);
                setIsOpen(false);
                onChange(selectedOptionValue);
            }
            else {
                if (offlineChangeModalContent) {
                    dispatch(setIsModalOpen(true));
                    dispatch(setModalContent(offlineChangeModalContent));
                }
            }
        }
        else {
            setSelectedOptionValue(selectedOptionValue);
            setIsOpen(false);
            onChange(selectedOptionValue);
        }
        setWasChanged(true);
    };
    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen((prev) => {
                const nextOpen = !prev;
                if (nextOpen) {
                    setFocusedIndex(0);
                    setOpenedByKeyboard(false);
                }
                else {
                    setFocusedIndex(-1);
                }
                return nextOpen;
            });
        }
    };
    const resetValue = (event) => {
        event.stopPropagation();
        if (!disabled) {
            event.stopPropagation();
            setSelectedOptionValue(null);
            onChange(null);
            setIsOpen(false);
        }
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            return;
        }
        if (!isOpen) {
            if (e.code === 'Space') {
                e.preventDefault();
                setIsOpen(true);
                setFocusedIndex(0);
                setOpenedByKeyboard(true);
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIndex((prev) => (prev + 1) % options.length);
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex((prev) => (prev - 1 + options.length) % options.length);
        }
        else if (e.key === ' ') {
            e.preventDefault();
            if (focusedIndex >= 0) {
                onOptionChange(options[focusedIndex].value);
            }
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
            setFocusedIndex(-1);
        }
    };
    return (_jsxs(_Fragment, { children: [label && (_jsxs("div", { className: "label", children: [label, tooltipContent && (_jsx(Tooltip, { content: tooltipContent, position: "top", children: _jsx(InfoIcon, { style: {
                                width: '16px',
                                height: '16px',
                                marginLeft: '4px',
                                cursor: 'help',
                                display: 'inline-block',
                                verticalAlign: 'middle',
                            } }) }))] })), _jsxs("div", { className: `select select--${background} size--${size} font-size--${fontSize}${isOpen ? ' is-open' : ''}`, ref: dropdownRef, style: { zIndex: zIndex }, children: [_jsxs("button", { ref: ref, type: 'button', "aria-haspopup": "listbox", "aria-expanded": isOpen, "aria-activedescendant": focusedIndex >= 0 ? `option-${options[focusedIndex].value}` : undefined, className: `${background === 'admin-white' ? 'admin-' : ''}select-value` +
                            `${highlightSelected ? (selectedOptionValue ? ' selected' : '') : ''}` +
                            `${error ? ' form-field-error' : ''}`, onClick: toggleDropdown, onKeyDown: handleKeyDown, children: [_jsx("span", { children: options.find((option) => option.value === selectedOptionValue)?.label ||
                                    placeholder ||
                                    t('placeholder.select_an_option') }), _jsx("span", { tabIndex: manageIsSelected && selectedOptionValue ? 0 : -1, className: "select-icon", onClick: manageIsSelected && selectedOptionValue ? resetValue : undefined, onKeyDown: (e) => {
                                    if (e.code === 'Space') {
                                        e.preventDefault();
                                        resetValue(e);
                                    }
                                }, children: manageIsSelected ? (selectedOptionValue ? (disabled ? (_jsx(ChevronIcon, {})) : (_jsx(RemoveIcon, {}))) : (_jsx(ChevronIcon, {}))) : (_jsx(ChevronIcon, {})) })] }), _jsx("div", { className: "select-options", children: _jsx("div", { className: "select-options-wrapper", role: "listbox", children: options.map((option, index) => {
                                const isSelected = option.value === selectedOptionValue;
                                const isFocused = index === focusedIndex;
                                return (_jsxs("div", { className: `option${isSelected ? ' option--selected' : ''}${openedByKeyboard && isFocused ? ' option--focused' : ''}`, onClick: () => onOptionChange(option.value), role: "option", "aria-selected": isSelected, children: [_jsx("span", { className: "option-checkbox" }), _jsx("span", { className: "option-label", children: option.label })] }, option.value));
                            }) }) })] }), error && errorMessage && _jsx("div", { className: 'form-field-error-message', children: errorMessage })] }));
});
export default Select;
